import { router } from "expo-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Image,
  Linking,
  Platform,
  Pressable,
  ScrollView,
  Text,
  TextInput,
  View,
  type TextStyle,
  type ViewStyle,
} from "react-native";

import { useAuth } from "../../../lib/authContext";
import { notifyAvatarChanged } from "../../../lib/avatarBus";
import {
  getMyJudgeProfile,
  upsertMyJudgeProfile,
  type UpdateJudgeProfileInput,
} from "../../../lib/judgeProfile";
import { pickAvatarFile, removeAvatar, uploadAvatar } from "../../../lib/profileUploads";
import { supabase } from "../../../lib/supabase";
import { FONTS } from "../../../lib/brand";
import { PANE_THEMES } from "../../../components/brand/paneThemes";

const THEME = PANE_THEMES.Judge;
const FOREST_MID = THEME.surfaceBg;
const AMBER = THEME.accent;
const SAGE = THEME.success;
const LINEN = THEME.textOnSurface;

const W = "rgba(255,255,255,1)";
const WS = "rgba(255,255,255,0.78)";
const WM = "rgba(255,255,255,0.55)";
const WL = "rgba(255,255,255,0.28)";
const BORDER = "rgba(255,255,255,0.07)";
const RED = THEME.destructive;

const PAGE_MAX_W = 720;
const BIO_LIMIT = 1200;

export default function JudgeProfileScreen() {
  const { user } = useAuth();

  const [headline, setHeadline] = useState("");
  const [organization, setOrganization] = useState("");
  const [bio, setBio] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const [expertiseInput, setExpertiseInput] = useState("");
  const [areas, setAreas] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [photoStatus, setPhotoStatus] = useState<
    | { kind: "idle" }
    | { kind: "uploading" }
    | { kind: "saved"; at: number }
    | { kind: "error"; message: string }
  >({ kind: "idle" });
  const [removeArmed, setRemoveArmed] = useState(false);
  const removeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const p = await getMyJudgeProfile();
      const { data: profileRow } = user
        ? await supabase
            .from("user_profiles")
            .select("avatar_url")
            .eq("id", user.id)
            .maybeSingle()
        : { data: null as { avatar_url?: string | null } | null };
      if (cancelled) return;
      if (p) {
        setHeadline(p.headline ?? "");
        setOrganization(p.organization ?? "");
        setBio(p.bio ?? "");
        setLinkedin(p.linkedin_url ?? "");
        setAreas(p.areas_of_expertise ?? []);
      }
      setAvatarUrl(
        typeof profileRow?.avatar_url === "string" && profileRow.avatar_url.trim()
          ? profileRow.avatar_url
          : null,
      );
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  // Disarm the remove button after 4s.
  useEffect(() => {
    if (!removeArmed) return;
    removeTimerRef.current = setTimeout(() => setRemoveArmed(false), 4000);
    return () => {
      if (removeTimerRef.current) clearTimeout(removeTimerRef.current);
    };
  }, [removeArmed]);

  // Auto-clear the "Saved." chip after 1.5s.
  useEffect(() => {
    if (photoStatus.kind !== "saved") return;
    const t = setTimeout(() => setPhotoStatus({ kind: "idle" }), 1500);
    return () => clearTimeout(t);
  }, [photoStatus]);

  async function onUploadPhoto() {
    setPhotoStatus({ kind: "idle" });
    const file = await pickAvatarFile();
    if (!file) return;
    if (typeof file.size === "number" && file.size > 5 * 1024 * 1024) {
      setPhotoStatus({ kind: "error", message: "That image is over 5 MB. Pick a smaller file." });
      return;
    }
    setPhotoStatus({ kind: "uploading" });
    try {
      const url = await uploadAvatar(file);
      // Cache-bust so the topbar/sidebar re-render the new image even if the
      // public URL is identical (same userId/avatar.jpg path with upsert: true).
      const bust = `${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}`;
      setAvatarUrl(bust);
      setPhotoStatus({ kind: "saved", at: Date.now() });
      if (user) notifyAvatarChanged(user.id, bust);
    } catch (e) {
      setPhotoStatus({
        kind: "error",
        message: e instanceof Error ? e.message : "Upload failed.",
      });
    }
  }

  async function onRemovePhotoClick() {
    if (!removeArmed) {
      setRemoveArmed(true);
      return;
    }
    setRemoveArmed(false);
    setPhotoStatus({ kind: "uploading" });
    try {
      await removeAvatar();
      setAvatarUrl(null);
      setPhotoStatus({ kind: "saved", at: Date.now() });
      if (user) notifyAvatarChanged(user.id, null);
    } catch (e) {
      setPhotoStatus({
        kind: "error",
        message: e instanceof Error ? e.message : "Could not remove photo.",
      });
    }
  }

  const fullName = useMemo(() => {
    const meta = (user?.user_metadata ?? {}) as Record<string, unknown>;
    return typeof meta.full_name === "string" ? meta.full_name : user?.email ?? "";
  }, [user]);

  const initials = useMemo(() => {
    const s = fullName.trim();
    if (!s) return "??";
    const parts = s.split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  }, [fullName]);

  function commitExpertiseInput() {
    const next = expertiseInput
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (next.length === 0) return;
    setAreas((prev) => {
      const set = new Set(prev);
      for (const n of next) set.add(n);
      return Array.from(set);
    });
    setExpertiseInput("");
  }

  function removeArea(a: string) {
    setAreas((prev) => prev.filter((x) => x !== a));
  }

  async function onSave() {
    setErr(null);
    setSaving(true);
    const payload: UpdateJudgeProfileInput = {
      headline: headline.trim() || null,
      organization: organization.trim() || null,
      bio: bio.trim() || null,
      linkedin_url: linkedin.trim() || null,
      areas_of_expertise: areas,
    };
    const { error } = await upsertMyJudgeProfile(payload);
    setSaving(false);
    if (error) {
      setErr(error.message);
      return;
    }
    setSavedAt(Date.now());
    setTimeout(() => setSavedAt(null), 2000);
  }

  if (loading) {
    return (
      <View style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
        <ActivityIndicator size="small" color={AMBER} />
      </View>
    );
  }

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingBottom: 80 }}>
      <View style={{ paddingHorizontal: 32, paddingTop: 28, maxWidth: PAGE_MAX_W }}>
        {/* Hero */}
        <View style={{ flexDirection: "row", alignItems: "center", gap: 8 }}>
          <View style={{ width: 22, height: 2, backgroundColor: AMBER, opacity: 0.85 }} />
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 10,
              letterSpacing: 3,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Judge profile
          </Text>
        </View>
        <Text
          style={{
            marginTop: 8,
            fontFamily: FONTS.displayLight,
            fontSize: 30,
            lineHeight: 36,
            color: LINEN,
          }}
        >
          Your judge profile.
        </Text>
        <Text
          style={{
            marginTop: 4,
            fontFamily: FONTS.bodyLight,
            fontSize: 13,
            color: WM,
            maxWidth: 600,
          }}
        >
          {"What clients see about you when you're invited to judge their program."}
        </Text>

        {/* Public profile card */}
        <View
          style={{
            marginTop: 22,
            padding: 20,
            borderRadius: 6,
            backgroundColor: FOREST_MID,
            borderWidth: 1,
            borderColor: BORDER,
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Public profile
          </Text>

          <View style={{ marginTop: 14 }}>
            <FieldLabel>Full name</FieldLabel>
            <Text
              style={{
                marginTop: 4,
                paddingVertical: 8,
                fontFamily: FONTS.displayMedium,
                fontSize: 18,
                color: LINEN,
              }}
            >
              {fullName || "—"}
            </Text>
            <Text style={{ fontFamily: FONTS.body, fontSize: 10.5, color: WL }}>
              From your account. Update via Settings → Security.
            </Text>
          </View>

          <View style={{ marginTop: 14 }}>
            <FieldLabel>Headline</FieldLabel>
            <TextInput
              value={headline}
              onChangeText={setHeadline}
              placeholder="VP Strategy at Acme Corp"
              placeholderTextColor={WL}
              style={inputStyle}
            />
          </View>

          <View style={{ marginTop: 14 }}>
            <FieldLabel>Organization</FieldLabel>
            <TextInput
              value={organization}
              onChangeText={setOrganization}
              placeholder="Acme Corp"
              placeholderTextColor={WL}
              style={inputStyle}
            />
          </View>

          <View style={{ marginTop: 14 }}>
            <FieldLabel>Bio</FieldLabel>
            <TextInput
              value={bio}
              onChangeText={(v) => setBio(v.slice(0, BIO_LIMIT))}
              multiline
              textAlignVertical="top"
              placeholder="Two or three sentences. Background, the work you've judged, what you look for."
              placeholderTextColor={WL}
              style={[inputStyle, { minHeight: 100, fontSize: 13, lineHeight: 19 }]}
            />
            <Text
              style={{
                marginTop: 4,
                textAlign: "right",
                fontFamily: FONTS.body,
                fontSize: 10.5,
                color: WL,
              }}
            >
              {bio.length} / {BIO_LIMIT}
            </Text>
          </View>

          <View style={{ marginTop: 14 }}>
            <FieldLabel>LinkedIn URL</FieldLabel>
            <TextInput
              value={linkedin}
              onChangeText={setLinkedin}
              autoCapitalize="none"
              keyboardType="url"
              placeholder="https://linkedin.com/in/yourname"
              placeholderTextColor={WL}
              style={inputStyle}
            />
            {linkedin.trim() ? (
              <Pressable
                onPress={() => {
                  if (Platform.OS === "web") {
                    if (typeof window !== "undefined") window.open(linkedin.trim(), "_blank");
                  } else {
                    void Linking.openURL(linkedin.trim());
                  }
                }}
                style={Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null}
              >
                <Text style={{ marginTop: 4, fontFamily: FONTS.bodyMedium, fontSize: 11, color: AMBER }}>
                  {"Open in a new tab →"}
                </Text>
              </Pressable>
            ) : null}
          </View>

          <View style={{ marginTop: 14 }}>
            <FieldLabel>Areas of expertise</FieldLabel>
            <TextInput
              value={expertiseInput}
              onChangeText={setExpertiseInput}
              onSubmitEditing={commitExpertiseInput}
              onBlur={commitExpertiseInput}
              placeholder="Comma-separated. Press enter or comma to add."
              placeholderTextColor={WL}
              style={inputStyle}
              autoCapitalize="words"
            />
            {areas.length > 0 ? (
              <View style={{ marginTop: 8, flexDirection: "row", flexWrap: "wrap", gap: 6 }}>
                {areas.map((a) => (
                  <Pressable
                    key={a}
                    onPress={() => removeArea(a)}
                    style={[
                      {
                        paddingHorizontal: 10,
                        paddingVertical: 5,
                        borderRadius: 999,
                        backgroundColor: "rgba(143,187,138,0.10)",
                        borderWidth: 1,
                        borderColor: "rgba(143,187,138,0.35)",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: 6,
                      } as ViewStyle,
                      Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                    ]}
                  >
                    <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11, color: SAGE }}>{a}</Text>
                    <Text style={{ fontFamily: FONTS.bodyBold, fontSize: 10, color: SAGE }}>×</Text>
                  </Pressable>
                ))}
              </View>
            ) : null}
          </View>
        </View>

        {/* Photo card */}
        <View
          style={{
            marginTop: 16,
            padding: 18,
            borderRadius: 6,
            backgroundColor: FOREST_MID,
            borderWidth: 1,
            borderColor: BORDER,
          }}
        >
          <Text
            style={{
              fontFamily: FONTS.bodyBold,
              fontSize: 9.5,
              letterSpacing: 2,
              textTransform: "uppercase",
              color: AMBER,
            }}
          >
            Photo
          </Text>

          <View style={{ marginTop: 14, flexDirection: "row", alignItems: "flex-start", gap: 18 }}>
            {/* Avatar */}
            <View
              style={{
                width: 88,
                height: 88,
                borderRadius: 44,
                backgroundColor: FOREST_MID,
                borderWidth: 1,
                borderColor: "rgba(181,135,58,0.30)",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {avatarUrl ? (
                <Image
                  source={{ uri: avatarUrl }}
                  style={{ width: 88, height: 88, borderRadius: 44 }}
                />
              ) : (
                <Text
                  style={{
                    fontFamily: FONTS.displaySemiBold,
                    fontSize: 32,
                    color: AMBER,
                  }}
                >
                  {initials}
                </Text>
              )}
            </View>

            {/* Right column */}
            <View style={{ flex: 1, gap: 8 }}>
              <Pressable
                onPress={() => void onUploadPhoto()}
                disabled={photoStatus.kind === "uploading"}
                style={[
                  {
                    alignSelf: "flex-start",
                    paddingHorizontal: 16,
                    paddingVertical: 9,
                    backgroundColor: AMBER,
                    borderRadius: 3,
                    flexDirection: "row",
                    alignItems: "center",
                    gap: 8,
                    opacity: photoStatus.kind === "uploading" ? 0.7 : 1,
                  } as ViewStyle,
                  Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                ]}
              >
                <Text
                  style={{
                    fontFamily: FONTS.bodyBold,
                    fontSize: 11,
                    letterSpacing: 1.3,
                    textTransform: "uppercase",
                    color: THEME.accentText,
                  }}
                >
                  Upload photo
                </Text>
              </Pressable>

              {avatarUrl ? (
                <Pressable
                  onPress={() => void onRemovePhotoClick()}
                  disabled={photoStatus.kind === "uploading"}
                  style={[
                    { alignSelf: "flex-start", paddingHorizontal: 4, paddingVertical: 4 } as ViewStyle,
                    Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
                  ]}
                >
                  <Text
                    style={{
                      fontFamily: FONTS.bodyMedium,
                      fontSize: 11.5,
                      color: removeArmed ? RED : WS,
                      textDecorationLine: "underline",
                    }}
                  >
                    {removeArmed ? "Click again to remove" : "Remove photo"}
                  </Text>
                </Pressable>
              ) : null}

              <Text
                style={{
                  marginTop: 2,
                  fontFamily: FONTS.body,
                  fontSize: 11,
                  color: WL,
                  lineHeight: 16,
                }}
              >
                JPG or PNG. Max 5 MB. Crops to a circle in the app.
              </Text>

              {photoStatus.kind === "uploading" ? (
                <View style={{ marginTop: 4, flexDirection: "row", alignItems: "center", gap: 8 }}>
                  <ActivityIndicator size="small" color={AMBER} />
                  <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11.5, color: WS }}>
                    Uploading…
                  </Text>
                </View>
              ) : null}
              {photoStatus.kind === "saved" ? (
                <Text style={{ marginTop: 4, fontFamily: FONTS.bodyMedium, fontSize: 11.5, color: SAGE }}>
                  Saved.
                </Text>
              ) : null}
              {photoStatus.kind === "error" ? (
                <Text style={{ marginTop: 4, fontFamily: FONTS.bodyMedium, fontSize: 11.5, color: RED }}>
                  {photoStatus.message}
                </Text>
              ) : null}
            </View>
          </View>
        </View>

        {/* Save row */}
        {err ? (
          <View
            style={{
              marginTop: 14,
              paddingHorizontal: 14,
              paddingVertical: 10,
              borderRadius: 4,
              borderWidth: 1,
              borderColor: "rgba(230,154,138,0.40)",
              backgroundColor: "rgba(230,154,138,0.08)",
            }}
          >
            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 12.5, color: RED }}>{err}</Text>
          </View>
        ) : null}

        <View style={{ marginTop: 18, flexDirection: "row", alignItems: "center", gap: 14 }}>
          <Pressable
            onPress={() => void onSave()}
            disabled={saving}
            style={[
              {
                paddingHorizontal: 22,
                paddingVertical: 11,
                backgroundColor: AMBER,
                borderRadius: 3,
                flexDirection: "row",
                alignItems: "center",
                gap: 8,
              } as ViewStyle,
              Platform.OS === "web" ? ({ cursor: "pointer" } as const) : null,
            ]}
          >
            {saving ? <ActivityIndicator size="small" color={THEME.accentText} /> : null}
            <Text
              style={{
                fontFamily: FONTS.bodyBold,
                fontSize: 11.5,
                letterSpacing: 1.4,
                textTransform: "uppercase",
                color: THEME.accentText,
              }}
            >
              {saving ? "Saving…" : "Save profile"}
            </Text>
          </Pressable>
          {savedAt ? (
            <Text style={{ fontFamily: FONTS.bodyMedium, fontSize: 11.5, color: SAGE }}>
              Saved.
            </Text>
          ) : null}
        </View>
      </View>
    </ScrollView>
  );
}

const inputStyle: TextStyle = {
  marginTop: 6,
  backgroundColor: "rgba(0,0,0,0.4)",
  borderWidth: 1,
  borderColor: BORDER,
  borderRadius: 3,
  paddingHorizontal: 12,
  paddingVertical: 10,
  color: W,
  fontFamily: FONTS.body,
  fontSize: 13,
};

function FieldLabel({ children }: { children: string }) {
  return (
    <Text
      style={{
        fontFamily: FONTS.bodyBold,
        fontSize: 9,
        letterSpacing: 1.6,
        textTransform: "uppercase",
        color: WM,
      }}
    >
      {children}
    </Text>
  );
}

void router;
